import React, { Component, ReactNode, ErrorInfo } from 'react';
import { safeSentryCapture } from '../../utils/sentry';
import { logger } from '@/utils/logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  timeout?: number;
  retryAttempts?: number;
}

interface State {
  hasCSSError: boolean;
  missingStyles: string[];
  isRetrying: boolean;
  retryCount: number;
  loadingState: 'loading' | 'success' | 'error' | 'timeout';
}

export class CSSLoadingErrorBoundary extends Component<Props, State> {
  private cssChecker: NodeJS.Timeout | null = null;
  private loadingTimeout: NodeJS.Timeout | null = null;
  private retryTimeout: NodeJS.Timeout | null = null;
  private fallbackStylesInjected = false;
  
  public state: State = {
    hasCSSError: false,
    missingStyles: [],
    isRetrying: false,
    retryCount: 0,
    loadingState: 'loading'
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    // Check if error is CSS-related
    if (error.message?.includes('CSS') || 
        error.message?.includes('stylesheet') ||
        error.message?.includes('FOUC') ||
        error.message?.includes('ChunkLoadError')) {
      return { 
        hasCSSError: true,
        loadingState: 'error'
      };
    }
    return {};
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (this.state.hasCSSError) {
      safeSentryCapture('captureException', error, {
        tags: {
          errorType: 'css_loading',
          component: 'CSSLoadingErrorBoundary',
          retryCount: this.state.retryCount
        },
        extra: {
          errorInfo,
          missingStyles: this.state.missingStyles,
          userAgent: navigator.userAgent,
          url: window.location.href
        }
      });
      
      // Inject fallback CSS immediately
      this.injectFallbackCSS();
      
      safeSentryCapture('captureException', error, {
        tags: {
          errorType: 'css_loading',
          component: 'CSSLoadingErrorBoundary',
          retryCount: this.state.retryCount
        },
        extra: {
          errorInfo,
          missingStyles: this.state.missingStyles,
          userAgent: navigator.userAgent,
          url: window.location.href
        }
      });
      
      // Attempt automatic retry if under limit
      this.attemptRetry();
    }
  }

  public componentDidMount() {
    // Set loading timeout
    const timeout = this.props.timeout || 5000;
    this.loadingTimeout = setTimeout(() => {
      if (this.state.loadingState === 'loading') {
        this.setState({ 
          hasCSSError: true, 
          loadingState: 'timeout',
          missingStyles: ['timeout_error']
        });
        this.injectFallbackCSS();
      }
    }, timeout);

    // Check for CSS loading every 200ms for better responsiveness
    this.cssChecker = setInterval(() => {
      this.checkCSSLoading();
    }, 200);

    // Stop checking after CSS loads successfully or timeout
    setTimeout(() => {
      if (this.cssChecker && this.state.loadingState === 'loading') {
        clearInterval(this.cssChecker);
        this.cssChecker = null;
        this.setState({ loadingState: 'success' });
      }
    }, timeout + 1000);

    // Listen for resource loading errors
    window.addEventListener('error', this.handleResourceError, true);
    
    // Listen for CSS load events
    this.monitorCSSLoading();
  }

  public componentWillUnmount() {
    if (this.cssChecker) {
      clearInterval(this.cssChecker);
      this.cssChecker = null;
    }
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = null;
    }
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }
    window.removeEventListener('error', this.handleResourceError, true);
  }

  private monitorCSSLoading = () => {
    // Monitor existing stylesheets
    const stylesheets = document.querySelectorAll('link[rel="stylesheet"]');
    stylesheets.forEach((link: HTMLLinkElement) => {
      link.addEventListener('load', () => {
        logger.debug('CSS loaded successfully', { href: link.href });
        if (this.state.loadingState === 'loading') {
          this.setState({ loadingState: 'success' });
        }
      });
      
      link.addEventListener('error', () => {
        safeSentryCapture('captureMessage', `CSS failed to load: ${link.href}`, {
          level: 'error',
          tags: { errorType: 'css_loading_failure', component: 'CSSLoadingErrorBoundary' },
          extra: { href: link.href }
        });
        this.setState(prevState => ({
          hasCSSError: true,
          loadingState: 'error',
          missingStyles: [...prevState.missingStyles, link.href]
        }));
        this.injectFallbackCSS();
      });
    });
  };

  private handleResourceError = (event: ErrorEvent) => {
    const target = event.target as HTMLLinkElement;
    
    if (target?.tagName === 'LINK' && target.rel === 'stylesheet') {
      logger.warn('CSS file failed to load', { href: target.href });
      
      this.setState(prevState => ({
        hasCSSError: true,
        loadingState: 'error',
        missingStyles: [...prevState.missingStyles, target.href]
      }));

      // Inject fallback CSS immediately
      this.injectFallbackCSS();

      safeSentryCapture('captureMessage', `CSS file failed to load: ${target.href}`, {
        level: 'warning',
        tags: {
          errorType: 'css_loading_failure',
          component: 'CSSLoadingErrorBoundary'
        },
        extra: {
          href: target.href,
          media: target.media,
          integrity: target.integrity,
          loadingState: this.state.loadingState
        }
      });
      
      // Attempt to retry loading
      this.attemptRetry();
    }
  };

  private checkCSSLoading = () => {
    try {
      // Create a test element to check if Tailwind classes are applied
      const testDiv = document.createElement('div');
      testDiv.className = 'hidden absolute bg-background text-foreground';
      testDiv.style.visibility = 'hidden';
      testDiv.style.position = 'absolute';
      testDiv.style.top = '-9999px';
      
      document.body.appendChild(testDiv);
      
      const styles = window.getComputedStyle(testDiv);
      const hasHiddenClass = styles.display === 'none';
      const hasAbsoluteClass = styles.position === 'absolute';
      const hasColorVars = styles.backgroundColor && styles.backgroundColor !== 'rgba(0, 0, 0, 0)';
      
      document.body.removeChild(testDiv);
      
      // If basic Tailwind classes don't work, CSS likely failed to load
      if (!hasHiddenClass || !hasAbsoluteClass || !hasColorVars) {
        logger.warn('CSS styles not properly loaded', {
          hasHiddenClass,
          hasAbsoluteClass,
          hasColorVars,
          backgroundColor: styles.backgroundColor
        });
        
        this.setState(prevState => ({
          hasCSSError: true,
          loadingState: 'error',
          missingStyles: [...prevState.missingStyles, 'tailwind_base_styles']
        }));

        this.injectFallbackCSS();

        safeSentryCapture('captureMessage', 'CSS styles not properly loaded', {
          level: 'warning',
          tags: {
            errorType: 'css_detection_failure',
            component: 'CSSLoadingErrorBoundary'
          },
          extra: {
            hasHiddenClass,
            hasAbsoluteClass,
            hasColorVars,
            displayValue: styles.display,
            positionValue: styles.position,
            backgroundColor: styles.backgroundColor
          }
        });
      } else if (this.state.loadingState === 'loading') {
        // CSS is working properly
        this.setState({ loadingState: 'success' });
        if (this.cssChecker) {
          clearInterval(this.cssChecker);
          this.cssChecker = null;
        }
      }
    } catch (error) {
      logger.error('Error checking CSS loading', error as Error);
      this.setState({ 
        hasCSSError: true, 
        loadingState: 'error',
        missingStyles: ['css_check_error']
      });
    }
  };

  private injectFallbackCSS = () => {
    if (this.fallbackStylesInjected) return;
    
    const fallbackCSS = `
      .css-loading-fallback {
        background: hsl(240 10% 3.9%);
        color: hsl(0 0% 98%);
        border: 1px solid hsl(240 3.7% 15.9%);
        font-family: system-ui, -apple-system, sans-serif;
        padding: 1rem;
        border-radius: 0.5rem;
        margin: 1rem;
        max-width: 600px;
      }
      
      .css-fallback-button {
        background: hsl(0 84% 60%);
        color: white;
        border: none;
        padding: 0.5rem 1rem;
        border-radius: 0.25rem;
        cursor: pointer;
        font-size: 0.875rem;
        margin-right: 0.5rem;
        transition: background-color 0.2s;
      }
      
      .css-fallback-button:hover {
        background: hsl(0 84% 50%);
      }
      
      .css-fallback-button:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    `;
    
    const style = document.createElement('style');
    style.id = 'css-loading-fallback';
    style.textContent = fallbackCSS;
    document.head.appendChild(style);
    
    this.fallbackStylesInjected = true;
    logger.debug('Fallback CSS injected successfully');
  };

  private attemptRetry = () => {
    const maxRetries = this.props.retryAttempts || 3;
    
    if (this.state.retryCount < maxRetries && !this.state.isRetrying) {
      this.setState({ isRetrying: true });
      
      logger.debug(`Attempting CSS retry ${this.state.retryCount + 1}/${maxRetries}`);
      
      this.retryTimeout = setTimeout(() => {
        // Force reload stylesheets
        const stylesheets = document.querySelectorAll('link[rel="stylesheet"]');
        stylesheets.forEach((link) => {
          const newLink = link.cloneNode() as HTMLLinkElement;
          newLink.href = `${link.getAttribute('href')}?retry=${Date.now()}`;
          link.parentNode?.insertBefore(newLink, link.nextSibling);
          link.remove();
        });
        
        this.setState(prevState => ({
          retryCount: prevState.retryCount + 1,
          isRetrying: false,
          hasCSSError: false,
          loadingState: 'loading'
        }));
        
        // Check if retry was successful
        setTimeout(() => {
          this.checkCSSLoading();
        }, 1000);
        
      }, 1000);
    }
  };

  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasCSSError) {
      return this.props.fallback || (
        <div className="css-loading-fallback">
          <h2 style={{ color: 'hsl(0 84% 60%)', marginBottom: '10px', fontSize: '1.25rem' }}>
            CSS Loading Error
          </h2>
          <p style={{ marginBottom: '10px' }}>
            The application's stylesheets failed to load properly. 
            {this.state.loadingState === 'timeout' && ' (Loading timeout)'}
            {this.state.loadingState === 'error' && ' (Network error)'}
          </p>
          
          {this.state.retryCount > 0 && (
            <p style={{ fontSize: '0.875rem', opacity: 0.8, marginBottom: '10px' }}>
              Retry attempts: {this.state.retryCount}/{this.props.retryAttempts || 3}
            </p>
          )}
          
          {this.state.missingStyles.length > 0 && (
            <details style={{ marginTop: '10px', marginBottom: '10px' }}>
              <summary style={{ cursor: 'pointer', fontSize: '0.875rem' }}>
                Missing Styles ({this.state.missingStyles.length})
              </summary>
              <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                {this.state.missingStyles.map((style, index) => (
                  <li key={index} style={{ fontSize: '0.75rem', opacity: 0.7, marginBottom: '2px' }}>
                    {style}
                  </li>
                ))}
              </ul>
            </details>
          )}
          
          <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
            {this.state.retryCount < (this.props.retryAttempts || 3) && !this.state.isRetrying && (
              <button
                onClick={this.attemptRetry}
                className="css-fallback-button"
                style={{ backgroundColor: 'hsl(142 76% 36%)' }}
                disabled={this.state.isRetrying}
              >
                {this.state.isRetrying ? 'Retrying...' : 'Retry Loading'}
              </button>
            )}
            
            <button
              onClick={this.handleReload}
              className="css-fallback-button"
            >
              Reload Page
            </button>
          </div>
          
          <div style={{ marginTop: '15px', fontSize: '0.75rem', opacity: 0.6 }}>
            <p>This error typically occurs due to:</p>
            <ul style={{ paddingLeft: '15px', marginTop: '5px' }}>
              <li>Network connectivity issues</li>
              <li>CDN or server problems</li>
              <li>Browser cache conflicts</li>
              <li>Ad blockers or security extensions</li>
            </ul>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}